<template>
  <div class="tw-flex tw-flex-col">
    <Loading loader="dots" :is-full-page="false" :active="!isReady" />

    <AppNavbar v-show="isReady" />

    <LeaveDatesTour v-if="isReady" />

    <div
      v-if="isReady"
      class="main-container tw-flex-wrap lg:tw-flex lg:tw-flex-no-wrap"
    >
      <RouterView
        :key="$route.fullPath"
        class="navbar-secondary-wrap tw-bg-white lg:tw-bg-transparent"
        name="secondary-nav"
      />

      <div class="lg:tw-flex-1 ie_app-container">
        <main>
          <div v-if="activeEmployment.hasAnnouncements">
            <AnnouncementList :announcements="activeEmployment.announcements" />
          </div>

          <div v-if="hasPaymentIssue && isAdmin">
            <div class="tw-mx-4">
              <PaymentIssue />
            </div>
          </div>
          <div v-else>
            <div v-if="!emailVerified">
              <div class="tw-mx-4">
                <AccountVerification />
              </div>
            </div>
            <div v-if="activeCompany.isSeatsExceeded()">
              <div v-if="isAdmin" class="tw-mx-4">
                <SeatsExceeded />
              </div>
            </div>
            <div v-else-if="!hasProperSubscription">
              <div class="tw-mx-4">
                <NeedsSubscription />
              </div>
            </div>
            <div v-if="activeEmployment.hasWarnings">
              <div>
                <UnavailableAllowanceYears
                  :warning="
                    activeEmployment.warnings.whereUnavailableAllowanceYears()[0]
                  "
                />
              </div>
            </div>
            <div>
              <CalendarServiceReConnector
                v-for="failedCalendarService in failedCalendarServices"
                :key="failedCalendarService.id"
                :calendar="failedCalendarService"
                @calendar-service-dismissed="onCalendarServiceDismissed"
              />
            </div>
          </div>

          <PortalTarget name="action-bar" />

          <transition name="page" mode="out-in">
            <RouterView class="tw-w-full tw-px-4" />
          </transition>
        </main>
      </div>
    </div>

    <VDialog :class="'prompt-dialog'" :click-to-close="false" />

    <AppModal v-if="isReady" />

    <HelpModal v-if="isReady" />

    <AppFooter v-if="isReady" />

    <SlideOver v-if="isReady" />
  </div>
</template>

<script>
import http, {
  HTTP_STATUS_AUTHENTICATION_EXPIRED,
  HTTP_STATUS_UNAUTHORIZED,
  isExpectedRequestFailure,
} from '@/plugins/http'
import { debounce, includes } from 'lodash-es'
import AppModal from '@/components/AppModal'
import HandleAuth from '@/mixins/HandleAuth'
import Subscription from '@/mixins/Subscription'
import AppFooter from '@/components/AppFooter'
import AppNavbar from '@/components/AppNavbar'
import HelpModal from '@/components/help/HelpModal'
import SlideOver from '@/components/SlideOver'
import LeaveDatesTour from '@/components/LeaveDatesTour'
import SystemCommonShortcuts from '@/mixins/page-shortcuts/CommonShortcuts'
import SlideoutPanel from '@/layouts/SlideoutPanel'
import Feed from '@/api/feed/Feed'
import EventBus from '@/plugins/event-bus'
import { mapActions, mapGetters } from 'vuex'
import Loading from 'vue-loading-overlay'
import UnavailableAllowanceYears from '@/components/UnavailableAllowanceYears'
import AnnouncementList from '@/components/AnnouncementList.vue'
import expiringStorage from '@/support/expiringStorage'

const NeedsSubscription = () => import('@/components/NeedsSubscription')
const SeatsExceeded = () => import('@/components/SeatsExceeded')
const AccountVerification = () => import('@/components/AccountVerification')
const CalendarServiceReConnector = () =>
  import('@/components/calendar-services/CalendarServiceReConnector')
const PaymentIssue = () => import('@/components/PaymentIssue')

export default {
  name: 'AppLayout',

  components: {
    Loading,
    AppModal,
    AppNavbar,
    HelpModal,
    SlideOver,
    AppFooter,
    LeaveDatesTour,
    NeedsSubscription,
    SeatsExceeded,
    AccountVerification,
    CalendarServiceReConnector,
    PaymentIssue,
    UnavailableAllowanceYears,
    AnnouncementList,
  },

  mixins: [HandleAuth, Subscription, SystemCommonShortcuts],

  data() {
    return {
      isLoading: false,
      dismissedServices:
        expiringStorage.get('dismissed_failed_calendar_services') ?? [],
    }
  },

  computed: {
    ...mapGetters('auth', ['companies', 'activeCompany']),

    failedCalendarServices() {
      return this.activeEmployment.failed_calendar_services.filter(
        calendar => !this.dismissedServices.includes(calendar.id)
      )
    },

    isAdmin() {
      return this.activeEmployment.is_admin
    },

    isReady() {
      return !this.isLoading && this.activeEmployment
    },
  },

  watch: {
    '$route.query': {
      handler: 'handleSlideoutPanel',
    },

    '$route.query.company': {
      async handler(companyKey) {
        this.isLoading = true

        if (!this.authUser.companies.length) {
          window.location.href = '/setup-company'
          return
        }

        if (companyKey && companyKey !== this.activeCompany?.id) {
          const company = this.authUser.companies.find(c => {
            return c.id === companyKey
          })

          if (!company) {
            let query = {
              ...this.$route.query,
              company: this.activeCompany?.id ?? this.companies[0].id,
            }

            this.$router.replace({ query }, () => {})

            return
          }

          await this.fetchCompany({ id: companyKey })
        }

        this.isLoading = false
        this.handleSlideoutPanel()
      },
      immediate: true,
    },
  },

  mounted() {
    this.fetchFeedPublishDate()

    const interval = setInterval(
      () => this.fetchFeedPublishDate(),
      5 * 60 * 60 * 1000
    )

    const onError = error => {
      if (
        error.response &&
        includes(
          [HTTP_STATUS_UNAUTHORIZED, HTTP_STATUS_AUTHENTICATION_EXPIRED],
          error.response.status
        )
      ) {
        location.href = '/signin?reason=session_timeout'
      }

      return Promise.reject(error)
    }

    const onSuccess = response => response

    const interceptor = http.interceptors.response.use(onSuccess, onError)

    this.$once('hook:beforeDestroy', () => {
      clearInterval(interval)
      http.interceptors.response.eject(interceptor)
    })

    this.removeConnectedCalendarServicesFromDismissedServices()
  },

  methods: {
    ...mapActions('auth', ['fetchCompany']),

    removeConnectedCalendarServicesFromDismissedServices() {
      const dismissed = expiringStorage.get(
        'dismissed_failed_calendar_services'
      )

      if (dismissed) {
        let activeList = this.activeEmployment.failed_calendar_services.map(
          calendar => calendar.id
        )

        let dismissedList = []
        this.dismissedServices.forEach(key => {
          if (activeList.includes(key)) {
            dismissedList.push(key)
          }
        })

        this.dismissedServices = dismissedList

        expiringStorage.set(
          'dismissed_failed_calendar_services',
          this.dismissedServices
        )
      }
    },

    handleSlideoutPanel: debounce(function() {
      new SlideoutPanel(this).handle()
    }, 50),

    onCalendarServiceDismissed(calendar) {
      this.dismissedServices.push(calendar.id)
      expiringStorage.set(
        'dismissed_failed_calendar_services',
        this.dismissedServices
      )
    },

    async fetchFeedPublishDate() {
      let feedPublishedAt

      try {
        feedPublishedAt = await Feed.getPublishedDate()
      } catch (error) {
        // The feed banner is decorative, so a cancelled or refused poll just
        // leaves the previous state in place. Anything else is a real fault
        // and is left to bubble up to Sentry.
        if (!isExpectedRequestFailure(error)) {
          throw error
        }

        return
      }

      if (feedPublishedAt === null) return

      EventBus.$emit('feed-updated', feedPublishedAt)
    },
  },
}
</script>
